import { useEffect, useState } from 'react'
import classnames from 'classnames/bind'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import Image from 'next/image'

// Components
import Popup from '@/components/common/Popup'

// Libs
import { useAppSelector } from '@/redux/hooks'
import { isClientSideShopApp } from '@/utils/navigator'

// Constants
import { ACCOUNT_NEED_UPGRADE_EVENT } from '@/constants/errorData'
import { ROLE_TYPE } from '@/constants/roleType'
import { ROUTE_PATH } from '@/constants/routePath'
import { STORAGE_DATA } from '@/constants/storageData'

// Styles
import registerStyles from '@/components/pages/ShopRegister/style.module.scss'

// Variables
const cx = classnames.bind(registerStyles)

// 登入/註冊/升級流程、領取特別禮頁與 /me（點「立即領取」的目的地）本身不跳（那些頁面就是在填會員資料）；livemallclub 沒有這些 route，filter 掉 undefined
const EXCLUDED_PATHS = [
  ROUTE_PATH?.LOGIN,
  ROUTE_PATH?.REGISTER,
  ROUTE_PATH?.FORGOT_PASSWORD,
  ROUTE_PATH?.UPGRADE,
  ROUTE_PATH?.SPECIAL_GIFT,
  ROUTE_PATH?.ME,
  ROUTE_PATH?.BRAND_LIVE_ROOM, // 品牌直播間不跳 Shop 特別禮
].filter(Boolean)

// Shop 自建登入 v2：已登入會員的 /v1/member/profile 回 isProfileCompleted: false 時，
// 跳「領取特別禮」Dialog 引導去填會員資料；「不再提醒」記 localStorage 後不再跳
const ProfileGiftDialog = () => {
  const { t } = useTranslation()
  const router = useRouter()
  const memberData = useAppSelector((state) => state.member?.memberData)
  const [isOpen, setIsOpen] = useState(false)
  const [needUpgrade, setNeedUpgrade] = useState(false)

  // 特別禮只給一般會員；商家角色不顯示
  const isMerchant = Array.isArray(memberData?.roles) && memberData.roles.includes(ROLE_TYPE.MERCHANT)
  const isProfileIncomplete = Boolean(memberData?.isFetched) && memberData?.isProfileCompleted === false && !isMerchant
  // 「不再提醒」綁 memberInfoId，避免換帳號時繼承上一個帳號的關閉狀態（不同帳號各自獨立、同帳號跨登入仍記得）
  const dismissKey = `${STORAGE_DATA.PROFILE_GIFT_REMINDER_DISMISSED}:${memberData?.memberInfoId ?? ''}`

  // 帳號需升級（v1 帳號打 API 被擋回 -20060）時，升級 Dialog 強制優先，特別禮一律不顯示
  // ponytail: 以本 session 收到過 need-upgrade event 為準；升級完成會重新登入/reload 清掉此狀態，若改成不 reload 的升級流程再改綁 memberInfoId
  useEffect(() => {
    const handleNeedUpgrade = () => {
      setNeedUpgrade(true)
      setIsOpen(false)
    }
    window.addEventListener(ACCOUNT_NEED_UPGRADE_EVENT, handleNeedUpgrade)
    return () => window.removeEventListener(ACCOUNT_NEED_UPGRADE_EVENT, handleNeedUpgrade)
  }, [])

  useEffect(() => {
    // App 內 webview 不跳（UA 含 tutorshop 或 window.TutorFlutter；pageProps 的 isWebView 沒人 set，不能用）
    if (isClientSideShopApp) return
    // 需升級時升級 Dialog 優先，特別禮不跳（-20060 事件或 profile 的 needs_upgrade 欄位皆算需升級）
    if (needUpgrade || memberData?.needsUpgrade) {
      setIsOpen(false)
      return
    }
    // 資料已完整（送出成功後重新 fetch profile 會讓這裡變 false）→ 關閉並不再跳
    if (!isProfileIncomplete) {
      setIsOpen(false)
      return
    }
    if (window.localStorage.getItem(dismissKey)) return
    if (EXCLUDED_PATHS.includes(router.pathname)) return

    setIsOpen(true)
  }, [isProfileIncomplete, router.pathname, dismissKey, needUpgrade, memberData?.needsUpgrade])

  const handleClaim = () => {
    setIsOpen(false)
    router.push(ROUTE_PATH.ME)
  }

  const handleDismissForever = () => {
    window.localStorage.setItem(dismissKey, 'true')
    setIsOpen(false)
  }

  return (
    <Popup
      title={t('Profile_Gift_Dialog_Title')}
      isOpen={isOpen}
      hasConfirm={false}
      hasCancel={false}
      canClose={false}
      wrapperClassname={cx('register-popup')}
      icon={
        <div className={cx('register-popup-icon')}>
          <Image src='/images/register-gift-icon.svg' alt='' width={34} height={34} />
        </div>
      }
    >
      <p className={cx('register-popup-description')}>{t('Profile_Gift_Dialog_Description')}</p>
      <div className={cx('register-email-popup-actions')}>
        <button type='button' className={cx('register-email-popup-confirm')} onClick={handleClaim}>
          {t('Profile_Gift_Dialog_Claim')}
        </button>
        <button type='button' className={cx('register-email-popup-cancel')} onClick={handleDismissForever}>
          {t('Profile_Gift_Dialog_Dismiss')}
        </button>
      </div>
    </Popup>
  )
}

export default ProfileGiftDialog
